import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-[#2d5f3f] text-[#2d5f3f] hover:bg-[#2d5f3f] hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <img
              src="https://static.wixstatic.com/media/5924bc_1d4a840f9cb44afcb7ee8f647411679d~mv2.png/v1/fill/w_212,h_93,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/5924bc_1d4a840f9cb44afcb7ee8f647411679d~mv2.png"
              alt="Equi-Go Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 font-['Playfair_Display']">
            Cookie Policy
          </h1>
          <p className="text-gray-600 mb-8">Last updated: January 2025</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. 
                They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Cookies</h2>
              <p>
                Equi-Go uses cookies to improve your experience on our website, remember your preferences, and understand 
                how visitors use our site. We use cookies for essential website functionality and to enhance your browsing experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Strictly Necessary Cookies</h3>
                  <p className="mb-3">
                    These cookies are essential for the website to function properly. They enable basic functions like page 
                    navigation and access to secure areas of the website.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Session Cookies:</strong> Maintain your session as you navigate the site</li>
                    <li><strong>Security Cookies:</strong> Authenticate users and prevent fraudulent use</li>
                    <li><strong>Form Cookies:</strong> Remember information you enter in forms</li>
                  </ul>
                  <p className="mt-3 text-sm text-gray-600"><strong>Duration:</strong> Session-based (deleted when you close your browser)</p>
                  <p className="text-sm text-gray-600"><strong>Legal Basis:</strong> These cookies do not require consent as they are essential for website operation</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Functional Cookies</h3>
                  <p className="mb-3">
                    These cookies allow the website to remember choices you make (such as your preferred language or region) 
                    and provide enhanced features.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                    <li><strong>Language Cookies:</strong> Remember your language selection</li>
                  </ul>
                  <p className="mt-3 text-sm text-gray-600"><strong>Duration:</strong> Up to 12 months</p>
                  <p className="text-sm text-gray-600"><strong>Legal Basis:</strong> Legitimate interest in providing personalized service</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Performance/Analytics Cookies</h3>
                  <p className="mb-3">
                    These cookies help us understand how visitors interact with our website by collecting and reporting 
                    information anonymously.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Analytics Cookies:</strong> Collect information about how you use the website</li>
                    <li><strong>Performance Cookies:</strong> Help us improve website functionality</li>
                  </ul>
                  <p className="mt-3 text-sm text-gray-600"><strong>Duration:</strong> Up to 26 months</p>
                  <p className="text-sm text-gray-600"><strong>Information Collected:</strong> Pages visited, time spent, browser type, device type, referring website</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 Third-Party Cookies</h3>
                  <p className="mb-3">
                    Some cookies are placed by third-party services that appear on our pages:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Social Media Cookies:</strong> From Facebook (if social media plugins are used)</li>
                    <li><strong>Google Maps:</strong> For displaying location maps</li>
                  </ul>
                  <p className="mt-3 text-sm text-gray-600">
                    We do not control these third-party cookies. Please check the respective third-party websites for more 
                    information about their cookies and how to manage them.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies We Do NOT Use</h2>
              <p className="mb-3">To respect your privacy, we do NOT use:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Advertising or tracking cookies</li>
                <li>Cookies that sell your data to third parties</li>
                <li>Cookies for behavioral advertising</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. How to Control Cookies</h2>
              <p className="mb-3">
                You have the right to decide whether to accept or reject cookies. You can control and manage cookies in several ways:
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">5.1 Browser Settings</h3>
                  <p className="mb-3">Most browsers allow you to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>View what cookies are stored and delete them individually</li>
                    <li>Block third-party cookies</li>
                    <li>Block all cookies from specific websites</li>
                    <li>Block all cookies</li>
                    <li>Delete all cookies when you close your browser</li>
                  </ul>
                  <p className="mt-3 text-sm text-gray-600">
                    <strong>Note:</strong> Blocking all cookies may affect your experience on our website and prevent you from 
                    accessing certain features.
                  </p>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Manage Cookies in Popular Browsers:</h3>
                  <ul className="space-y-2">
                    <li>
                      <strong>Google Chrome:</strong>{' '}
                      <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#2d5f3f] hover:underline">
                        Cookie settings
                      </a>
                    </li>
                    <li>
                      <strong>Mozilla Firefox:</strong>{' '}
                      <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-[#2d5f3f] hover:underline">
                        Cookie settings
                      </a>
                    </li>
                    <li>
                      <strong>Safari:</strong>{' '}
                      <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#2d5f3f] hover:underline">
                        Cookie settings
                      </a>
                    </li>
                    <li>
                      <strong>Microsoft Edge:</strong>{' '}
                      <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#2d5f3f] hover:underline">
                        Cookie settings
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Do Not Track (DNT)</h2>
              <p>
                Some browsers have a "Do Not Track" feature that signals to websites that you do not want your online activity 
                tracked. Our website currently does not respond to DNT signals, but we do not track your activity across 
                third-party websites.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in technology or legal requirements. 
                We will post any changes on this page with an updated "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. More Information</h2>
              <p className="mb-3">
                For more information about how we handle your personal data, please see our{' '}
                <a href="/privacy-policy" className="text-[#2d5f3f] hover:underline">Privacy Policy</a>.
              </p>
              <p>
                For general information about cookies, visit{' '}
                <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#2d5f3f] hover:underline">
                  www.allaboutcookies.org
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
              <p className="mb-3">If you have questions about our use of cookies, please contact us:</p>
              <div className="bg-gray-50 p-6 rounded-xl">
                <p className="font-semibold text-gray-900">Equi-Go Horsebox Hire</p>
                <p>North Yorkshire, between Harrogate and Boroughbridge</p>
                <p className="mt-2">
                  Email: <a href="mailto:enquiries@equi-go.co.uk" className="text-[#2d5f3f] hover:underline">enquiries@equi-go.co.uk</a>
                </p>
                <p>
                  Phone: <a href="tel:07788754165" className="text-[#2d5f3f] hover:underline">07788 754165</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;